import Sortable from "sortablejs";
import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

interface TreeItem {
    id: number;
    parent_id: number | null;
    sort_order: number;
}

/**
 * 페이지·메뉴 트리 드래그앤드롭 정렬 (부모/자식 중첩).
 *
 * 루트 <ul data-page-tree-root> 와 각 항목의 <ul data-page-children> 를 같은 Sortable 그룹으로 묶어
 * 드래그로 순서/부모를 바꾼 뒤, 트리 전체를 직렬화해 서버에 저장한다.
 *
 *   PATCH(=POST + X-HTTP-Method-Override) {reorderUrl}
 *   body: { items: [{ id, parent_id, sort_order }, ...] }
 */
export class PageTreeSortable {
    readonly #root: HTMLElement;
    readonly #endpoint: string;
    readonly #csrf: string;
    readonly #instances: Sortable[] = [];
    static readonly #GROUP = "page-tree";
    static readonly #MAX_DEPTH = 3;

    constructor(root: HTMLElement) {
        this.#root = root;
        this.#endpoint = root.dataset.reorderUrl ?? "";
        this.#csrf = this.#readCsrf();
    }

    #readCsrf(): string {
        return (
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
            document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
            ""
        );
    }

    init(): this {
        if (!this.#endpoint) return this;
        const lists = this.#root.querySelectorAll<HTMLElement>(
            "[data-page-tree-root], [data-page-children]"
        );
        lists.forEach((ul) => {
            const cfg: Sortable.Options = {
                group: PageTreeSortable.#GROUP,
                animation: 150,
                fallbackOnBody: true,
                swapThreshold: 0.6,
                handle: "[data-page-drag-handle]",
                draggable: ".admin-page-tree__item",
                ghostClass: "admin-page-tree__ghost",
                dragClass: "admin-page-tree__drag",
                onMove: (evt) => this.#allowMove(evt),
                onEnd: () => void this.#save(),
            };
            this.#instances.push(Sortable.create(ul, cfg));
        });
        return this;
    }

    /** 최대 깊이 제한 — 끌어 놓을 리스트의 중첩 깊이가 한도를 넘으면 거부. */
    #allowMove(evt: Sortable.MoveEvent): boolean {
        let depth = 1;
        let el: HTMLElement | null = evt.to.parentElement;
        while (el && el !== this.#root) {
            if (el.classList && el.classList.contains("admin-page-tree__item")) depth++;
            el = el.parentElement;
        }
        return depth <= PageTreeSortable.#MAX_DEPTH;
    }

    #serialize(): TreeItem[] {
        const out: TreeItem[] = [];
        const walk = (ul: HTMLElement, parentId: number | null): void => {
            const items = Array.from(ul.children).filter((c) =>
                (c as HTMLElement).classList.contains("admin-page-tree__item")
            ) as HTMLElement[];
            items.forEach((li, i) => {
                const id = parseInt(li.dataset.id ?? "0", 10);
                if (!id) return;
                out.push({ id, parent_id: parentId, sort_order: (i + 1) * 10 });
                const childUl = li.querySelector<HTMLElement>(":scope > [data-page-children]");
                if (childUl) walk(childUl, id);
            });
        };
        const rootUl = this.#root.querySelector<HTMLElement>("[data-page-tree-root]");
        if (rootUl) walk(rootUl, null);
        return out;
    }

    async #save(): Promise<void> {
        const items = this.#serialize();
        if (items.length === 0) return;

        this.#setLoader(true);
        try {
            const res = await fetch(this.#endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-HTTP-Method-Override": "PATCH",
                    "X-CSRF-TOKEN": this.#csrf,
                },
                body: JSON.stringify({ items, _token: this.#csrf }),
            });
            const json = (await res.json().catch(() => ({}))) as { result?: string; msg?: string };
            if (res.ok && json.result === "success") {
                Toast.success("순서가 저장되었습니다.");
            } else {
                await Modal.alert(json.msg ?? "순서 저장에 실패했습니다. 새로고침 후 다시 시도해주세요.");
                window.location.reload();
            }
        } catch {
            await Modal.alert("순서 저장 중 오류가 발생했습니다.");
            window.location.reload();
        } finally {
            this.#setLoader(false);
        }
    }

    #setLoader(on: boolean): void {
        document.documentElement.classList.toggle("admin--loading", on);
    }

    destroy(): void {
        this.#instances.forEach((s) => s.destroy());
        this.#instances.length = 0;
    }
}

interface PageTreeRoot extends HTMLElement {
    _pageTreeInited?: boolean;
}

export function initPageTree(): void {
    document.querySelectorAll<PageTreeRoot>("[data-page-tree]").forEach((el) => {
        if (el._pageTreeInited) return;
        el._pageTreeInited = true;
        new PageTreeSortable(el).init();
    });
}
